import * as Config from '../core/config.js';
import { EventBus, EVENTS } from '../services/EventBus.js';
import { getLayoutCache, getViewMode } from '../rendering/renderer.js';
import { isOrbitViewMode } from '../rendering/viewModes.js';
import { textureCoordsToGridCoords, getGridWorldBounds } from '../utils/utils.js';

import { PanStrategy } from './inputStrategies/PanStrategy.js';
import { ShiftWorldStrategy } from './inputStrategies/ShiftWorldStrategy.js';
import { DrawStrategy } from './inputStrategies/DrawStrategy.js';
import { PlacePatternStrategy } from './inputStrategies/PlacePatternStrategy.js';
import { SelectRegionStrategy } from './inputStrategies/SelectRegionStrategy.js';
import { HoverHandler } from './inputStrategies/HoverHandler.js';
import { OrbitStrategy } from './inputStrategies/OrbitStrategy.js';

/**
 * @class InputManager
 * @description Manages all user input on the canvas by delegating to specific strategy classes.
 */
export class InputManager {
    constructor(canvas, worldManager, appContext, isMobile = false) {
        this.canvas = canvas;
        this.worldManager = worldManager;
        this.appContext = appContext;
        this.gl = canvas.getContext('webgl2');
        this.isMobile = isMobile;
        this.layoutCache = getLayoutCache(); 
        this.strategies = {
            pan: new PanStrategy(this),
            shiftWorld: new ShiftWorldStrategy(this),
            draw: new DrawStrategy(this),
            place: new PlacePatternStrategy(this),
            select: new SelectRegionStrategy(this),
            orbit: new OrbitStrategy(this),
        };
        // While the "shift world" hotkey is held we temporarily swap into shiftWorld mode and
        // restore the prior mode on release. Tracks that this is a held-key override (not a normal
        // mode change) so an unrelated INTERACTION_MODE_CHANGED can't strand us in shiftWorld.
        this._shiftWorldKeyHeld = false;
        this._strategyBeforeShiftWorld = null;
        this._strategyBeforeOrbit = null;
        this.hoverHandler = new HoverHandler(this);

        this.currentStrategyName = 'pan'; 
        this.previousStrategyName = 'pan';
        this.currentStrategy = this.strategies.pan;

        this._calculateGridBounds();
        this._setupListeners();
        if (isOrbitViewMode(getViewMode()) && !this.isMobile) {
            this._handleViewModeChanged({ mode: getViewMode() });
        }
    }

    _setupListeners() {
        this.canvas.addEventListener('mousedown', (e) => this.currentStrategy.handleMouseDown(e));
        this.canvas.addEventListener('mousemove', (e) => {
            
            if (this.currentStrategyName === 'pan' || this.currentStrategyName === 'draw') {
                this.hoverHandler.scheduleHoverUpdate(e);
            }
            
            this.currentStrategy.handleMouseMove(e);
        });

        this.canvas.addEventListener('mouseup', (e) => {
             
            if (this.currentStrategyName === 'draw') {
                this.hoverHandler.scheduleHoverUpdate(e);
            }
            this.currentStrategy.handleMouseUp(e);
        });

        
        this.canvas.addEventListener('mouseout', (e) => {
            this.hoverHandler.cancelHoverUpdate();
            this.currentStrategy.handleMouseOut(e);
        });
        this.canvas.addEventListener('wheel', (e) => {
            e.preventDefault();
            if (this.isMobile) return; 
            const { viewType } = this.getCoordsFromPointerEvent(e);
            if (viewType !== 'selected') return;
            if (this.currentStrategyName === 'orbit') {
                this.currentStrategy.handleMouseWheel(e);
                return;
            }
            if (e.ctrlKey || e.shiftKey) {
                const scrollAmount = Math.sign(e.deltaY);
                EventBus.dispatch(EVENTS.COMMAND_INCREMENT_BRUSH_SIZE, -scrollAmount);
                this.currentStrategy.handleMouseMove(e);
            } else {
                const zoomFactor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
                this.zoomAtPoint(e.clientX, e.clientY, zoomFactor);
            }
        }, { passive: false });
        this.canvas.addEventListener('contextmenu', e => e.preventDefault());
        this.canvas.addEventListener('touchstart', (e) => {
            if (e.target.closest('.mobile-fab-container')) return; 
            e.preventDefault();
            this.currentStrategy.handleTouchStart(e);
        }, { passive: false });
        this.canvas.addEventListener('touchmove', (e) => {
            if (e.target.closest('.mobile-fab-container')) return;
            e.preventDefault();
            this.currentStrategy.handleTouchMove(e);
        }, { passive: false });
        this.canvas.addEventListener('touchend', (e) => {
            if (e.target.closest('.mobile-fab-container')) return;
            e.preventDefault();
            this.currentStrategy.handleTouchEnd(e);
        }, { passive: false });
        this.canvas.addEventListener('touchcancel', (e) => {
            if (e.target.closest('.mobile-fab-container')) return;
            e.preventDefault();
            this.currentStrategy.handleTouchEnd(e);
        }, { passive: false });
        // Capture phase so the active strategy can intercept and consume a key (via
        // stopPropagation) before the global KeyboardShortcutManager's bubble-phase handler —
        // e.g. 'r' rotates while placing a pattern instead of resetting all worlds.
        document.addEventListener('keydown', (e) => this.currentStrategy.handleKeyDown(e), true);
        // Hold-to-shift-world hotkey: while held, dragging the selected view translates the world's
        // cells (wrapping at the seam) instead of doing the active mode's action.
        window.addEventListener('keydown', (e) => this._handleShiftWorldKeyDown(e));
        window.addEventListener('keyup', (e) => this._handleShiftWorldKeyUp(e));
        window.addEventListener('blur', () => this._releaseShiftWorldKey());
        EventBus.subscribe(EVENTS.INTERACTION_MODE_CHANGED, (mode) => {
            if (!this._isOrbiting()) this.setStrategy(mode);
        });
        EventBus.subscribe(EVENTS.COMMAND_ENTER_PLACING_MODE, (data) => {
            if (!this._isOrbiting()) this.setStrategy('place', data);
        });
        EventBus.subscribe(EVENTS.COMMAND_START_PATTERN_CAPTURE, (data) => {
            if (!this._isOrbiting()) this.setStrategy('select', data);
        });
        EventBus.subscribe(EVENTS.LAYOUT_CALCULATED, (newLayout) => { this.layoutCache = newLayout; });
        EventBus.subscribe(EVENTS.VIEW_MODE_CHANGED, (data) => this._handleViewModeChanged(data));
    }

    /** True while a 3D projection owns the pointer, so flat-grid tools must not take the strategy. */
    _isOrbiting() {
        return isOrbitViewMode(getViewMode());
    }

    /**
     * Sets the active input handling strategy.
     * @param {string} name - The name of the strategy to activate ('pan', 'draw', 'place').
     * @param {object} [options] - Optional data to pass to the new strategy's `enter` method.
     */
    setStrategy(name, options) {
        if (!this.strategies[name] || this.currentStrategyName === name) {
            return;
        }
        if (this.currentStrategyName !== 'place') {
            this.previousStrategyName = this.currentStrategyName;
        }
        this.currentStrategy.exit();
        this.currentStrategyName = name;
        this.currentStrategy = this.strategies[name];
        this.currentStrategy.enter(options);
    }

    _handleViewModeChanged({ mode }) {
        // Resolve a held-H override before changing the underlying navigation strategy. Otherwise
        // releasing H after switching the view could restore orbit in Flat mode (or pan in 3D).
        if (this._shiftWorldKeyHeld) this._releaseShiftWorldKey();
        const orbit = isOrbitViewMode(mode);
        if (orbit && !this.isMobile) {
            if (this.currentStrategyName === 'orbit') return;
            // A 3D mode deliberately cancels transient placing/selecting/drawing gestures.
            // Those strategies need entry data that cannot be reconstructed on return, while
            // previousStrategyName is their stable navigation mode.
            this._strategyBeforeOrbit = this.currentStrategyName === 'pan'
                ? 'pan'
                : this.previousStrategyName || 'pan';
            this.setStrategy('orbit');
            return;
        }
        if (!orbit && this.currentStrategyName === 'orbit') {
            const restore = this._strategyBeforeOrbit || 'pan';
            this._strategyBeforeOrbit = null;
            this.setStrategy(restore);
        }
    }
    
    

    /** The key that, while held, enables world-shift (cell-translate) dragging. */
    static get SHIFT_WORLD_KEY() { return 'h'; }

    _isTextInputFocused() {
        const el = document.activeElement;
        return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' ||
            el.tagName === 'SELECT' || el.isContentEditable);
    }

    _handleShiftWorldKeyDown(event) {
        if (event.key !== InputManager.SHIFT_WORLD_KEY) return;
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        if (this._shiftWorldKeyHeld || this._isTextInputFocused()) return;
        // Only override the navigation modes, so placing/selecting a pattern is left intact.
        if (this.currentStrategyName !== 'pan' && this.currentStrategyName !== 'orbit') return;
        event.preventDefault();
        this._shiftWorldKeyHeld = true;
        this._strategyBeforeShiftWorld = this.currentStrategyName;
        this.setStrategy('shiftWorld');
    }

    _handleShiftWorldKeyUp(event) {
        if (event.key !== InputManager.SHIFT_WORLD_KEY) return;
        this._releaseShiftWorldKey();
    }

    _releaseShiftWorldKey() {
        if (!this._shiftWorldKeyHeld) return;
        this._shiftWorldKeyHeld = false;
        const restore = this._strategyBeforeShiftWorld || 'pan';
        this._strategyBeforeShiftWorld = null;
        // Only restore if we're still the active override (a mode change mid-hold takes precedence).
        if (this.currentStrategyName === 'shiftWorld') this.setStrategy(restore);
    }

    getCoordsFromPointerEvent(event) {
        if (!this.gl || !this.gl.canvas || !this.worldManager || !this.layoutCache.selectedView) {
            return { worldIndexAtCursor: null, col: null, row: null, viewType: null };
        }
        const camera = this.worldManager.getCurrentCameraState();
        if (!camera) return { worldIndexAtCursor: null, col: null, row: null, viewType: null };
        const rect = this.gl.canvas.getBoundingClientRect();
        const pointerX = event.clientX - rect.left;
        const pointerY = event.clientY - rect.top;
        // Minimaps are hit-tested first: in the near-square layout the minimap is docked as an
        // overlay on top of the selected view, so its corner must win. In the side-by-side and
        // stacked layouts the regions are disjoint, so checking order is irrelevant there.
        const { gridContainerX, gridContainerY, miniMapW, miniMapH, miniMapSpacing } = this.layoutCache.miniMap;
        for (let i = 0; i < Config.NUM_WORLDS; i++) {
            const r_map = Math.floor(i / Config.WORLD_LAYOUT_COLS);
            const c_map = i % Config.WORLD_LAYOUT_COLS;
            const miniX = gridContainerX + c_map * (miniMapW + miniMapSpacing);
            const miniY = gridContainerY + r_map * (miniMapH + miniMapSpacing);
            if (pointerX >= miniX && pointerX < miniX + miniMapW && pointerY >= miniY && pointerY < miniY + miniMapH) {
                return { worldIndexAtCursor: i, col: null, row: null, viewType: 'mini' };
            }
        }
        const { x: selectedViewX, y: selectedViewY, width: selectedViewWidth, height: selectedViewHeight } = this.layoutCache.selectedView;
        if (pointerX >= selectedViewX && pointerX < selectedViewX + selectedViewWidth &&
            pointerY >= selectedViewY && pointerY < selectedViewY + selectedViewHeight) {
            const texCoordX = (pointerX - selectedViewX) / selectedViewWidth;
            const texCoordY = (pointerY - selectedViewY) / selectedViewHeight;
            return { ...textureCoordsToGridCoords(texCoordX, texCoordY, camera), viewType: 'selected', worldIndexAtCursor: this.worldManager.getSelectedWorldIndex() };
        }
        return { worldIndexAtCursor: null, col: null, row: null, viewType: null };
    }
    
    zoomAtPoint(pivotClientX, pivotClientY, zoomFactor) {
        const camera = this.worldManager.getCurrentCameraState();
        if (!camera) return;
        const { worldX, worldY } = this.getCoordsFromPointerEvent({ clientX: pivotClientX, clientY: pivotClientY });
        if (worldX === null) return;
        const oldZoom = camera.zoom;
        const newZoom = Math.max(1.0, Math.min(25.0, oldZoom * zoomFactor));
        if (newZoom !== oldZoom) {
            const ratio = oldZoom / newZoom;
            camera.x = worldX * (1 - ratio) + camera.x * ratio;
            camera.y = worldY * (1 - ratio) + camera.y * ratio;
            camera.zoom = newZoom;
            this.clampCameraPan();
            EventBus.dispatch(EVENTS.CAMERA_CHANGED, {
                zoom: newZoom,
                worldIndex: this.worldManager.getSelectedWorldIndex(),
            });
        }
    }
    
    handleBrushSizeWheel(event) {
        if (event.ctrlKey) {
            const scrollAmount = Math.sign(event.deltaY);
            const newSize = this.appContext.brushController.getBrushSize() - scrollAmount;
            EventBus.dispatch(EVENTS.COMMAND_SET_BRUSH_SIZE, newSize);
        }
    }

    _calculateGridBounds() {
        // Single-sourced from utils so the clamp's fallback center (grid smaller than the view)
        // lands exactly on the same point as the default camera — otherwise the first zoom
        // in/out visibly nudges an untouched view.
        this.gridWorldBounds = getGridWorldBounds();
    }
    
    clampCameraPan() {
        const camera = this.worldManager.getCurrentCameraState();
        if (!camera) return;
        const { RENDER_TEXTURE_SIZE } = Config;
        const viewWidth = RENDER_TEXTURE_SIZE / camera.zoom;
        const viewHeight = RENDER_TEXTURE_SIZE / camera.zoom;
        const minX = this.gridWorldBounds.minX + viewWidth / 2;
        const maxX = this.gridWorldBounds.maxX - viewWidth / 2;
        const minY = this.gridWorldBounds.minY + viewHeight / 2;
        const maxY = this.gridWorldBounds.maxY - viewHeight / 2;
        camera.x = (minX > maxX) ? (minX + maxX) / 2 : Math.max(minX, Math.min(maxX, camera.x));
        camera.y = (minY > maxY) ? (minY + maxY) / 2 : Math.max(minY, Math.min(maxY, camera.y));
    }
}
